import { FormEvent, useCallback, useState } from 'react';
import { useNavigate } from 'react-router';
import classNames from 'classnames';
import { useClientContext } from 'client/context';
import { UserAuth } from 'client/voladeum/auth/types';
import { setToken } from 'ducks/user/slices';
import { Button } from 'framework/button';
import { Input } from 'framework/Input';
import { useThemeContext } from 'framework/theme/context/ThemeContext';
import { PUBLIC } from 'router/routes';
import { useAppDispatch } from 'store/hooks';

import { GoogleButton } from 'components/google/GoogleButton';

import { auth_default } from './defaults';
import { CAROUSEL } from './enums';

import cn from './LoginPage.module.scss';
const { PUBLIC_URL } = process.env;

export default () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [form, setForm] = useState<UserAuth>({ ...auth_default });
  const [carousel, setCarousel] = useState(CAROUSEL.ONE);
  const { Voladeum, handleError } = useClientContext();
  const { theme } = useThemeContext();

  const onChange = useCallback(
    (auth: Partial<UserAuth>) => setForm((prev) => ({ ...prev, ...auth })),
    []
  );

  const onClick = useCallback(async () => {
    try {
      const res = await Voladeum.auth.guest();
      dispatch(setToken({ token: res.data }));
    } catch (err) {
      handleError(err);
    }
  }, [Voladeum.auth, dispatch, handleError]);

  const onSubmit = useCallback(
    async (e: FormEvent<HTMLFormElement>) => {
      try {
        e.preventDefault();
        const payload = { login: form.login, password: form.password };

        const res = await Voladeum.auth.login(payload);
        dispatch(setToken({ token: res.data }));
      } catch (err) {
        handleError(err);
      }
    },
    [Voladeum, dispatch, handleError, form]
  );

  return (
    <div className={cn.page}>
      <div className={cn.information}>
        <div
          className={cn.carousel}
          style={{
            backgroundImage: `url(${process.env.PUBLIC_URL}/pattern.png)`,
            border: theme.field.border.normal,
          }}
        >
          <div className={cn.logo}>
            <img src={`${PUBLIC_URL}/voladeum/voladeum.png`} alt="Voladeum" />
          </div>
          <div className={cn.content}>
            <h1>Backtest Smarter, Not Harder</h1>
            <h2>
              Test your trading ideas instantly without an account, and upgrade to Voladeum’s
              advanced suite of tools whenever you’re ready.
            </h2>
            <Button margin="8px 0px 0px 0px" onClick={onClick} shadow>
              Instant Access
            </Button>
          </div>
          <div className={cn.items}>
            <div
              className={classNames(cn.control, { [cn.active]: carousel === CAROUSEL.ONE })}
              onClick={() => setCarousel(CAROUSEL.ONE)}
            >
              <div className={cn.tab} />
            </div>
            <div
              className={classNames(cn.control, { [cn.active]: carousel === CAROUSEL.TWO })}
              onClick={() => setCarousel(CAROUSEL.TWO)}
            >
              <div className={cn.tab} />
            </div>
            <div
              className={classNames(cn.control, { [cn.active]: carousel === CAROUSEL.THREE })}
              onClick={() => setCarousel(CAROUSEL.THREE)}
            >
              <div className={cn.tab} />
            </div>
          </div>
        </div>
      </div>
      <div className={cn.login}>
        <form className={cn.form} onSubmit={onSubmit}>
          <div className={cn.logo}>
            <img src={`${PUBLIC_URL}/voladeum/voladeum-vertical.png`} alt="Voladeum" />
          </div>
          <Input
            height="34px"
            onChange={({ value }) => onChange({ login: value })}
            value={form.login}
            placeholder="Email Address"
          />
          <Input
            height="34px"
            onChange={({ value }) => onChange({ password: value })}
            value={form.password}
            placeholder="Password"
            type="password"
          />
          <Button type="submit" width="100%" height="36px" shadow>
            Sign in
          </Button>
          <GoogleButton />
          <div className={cn.row}>
            <div className={cn.break} />
            <p className={cn.text}>OR</p>
            <div className={cn.break} />
          </div>

          <Button
            width="100%"
            height="36px"
            onClick={() => navigate(PUBLIC.CREATE_ACCOUNT.path)}
            variant="secondary"
            shadow
          >
            Create account
          </Button>
        </form>
        <a className={cn.forgot} onClick={() => navigate(PUBLIC.PASSWORD_REQUEST.path)}>
          Forgot Password?
        </a>
      </div>
    </div>
  );
};
